import { CircularProgress, Tooltip } from '@civilgrid/ui/components';
import type { MultiPolygon } from 'geojson';
import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useAsyncRetry, useTimeoutFn } from 'react-use';
import { useSnapshot } from 'valtio';
import { getAgenciesSummary } from '~/api/misc2';
import Button from '~/components2/forms/Button';
import CloseIconButton from '~/components2/forms/CloseIconButton';
import { mapState } from '~/components2/Map/Map';
import { METADATA_SIDEBAR_WIDTH } from '~/components2/Map/Widgets/MetadataSidebar/MetadataSidebar';
import { isMetadataSidebarOpen } from '~/components2/Map/Widgets/MetadataSidebar/metadataSidebar.helpers';
import { useViewMode } from '../hooks';
import { homeMapStore } from '../store';
import SummaryPill from './SummaryPill';

const MIN_ZOOM = 17;
const STORAGE_KEY = 'agenciesSummaryOpen';

// Right offset (px) that shifts this panel left to clear the docked metadata sidebar,
// approximating Map.tsx's metadataSidebarOpen. Can't see isStyleLoaded/showLayerDetails
// here, so gate on a real point only. Closed: 16px; open: sidebar width + 16px.
function useMetadataSidebarRightOffset(): number {
  const { identifyDetailsPoint } = useSnapshot(homeMapStore);
  const sidebarOpen = isMetadataSidebarOpen(identifyDetailsPoint);
  return sidebarOpen ? METADATA_SIDEBAR_WIDTH + 16 : 16;
}

export default function AgenciesSummaryComponent() {
  const [isOpen, _setIsOpen] = useState<boolean>(() => {
    const isClosed = sessionStorage.getItem(STORAGE_KEY) === 'false';
    return !isClosed;
  });
  const {
    viewState: { zoom },
  } = useSnapshot(mapState);
  const metadataSidebarRight = useMetadataSidebarRightOffset();

  const setIsOpen = (open: boolean) => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(open));
    _setIsOpen(open);
  };

  // don't render anything if the zoom is too low
  if (zoom <= MIN_ZOOM) return null;

  if (isOpen) return <AgenciesSummary setIsOpen={setIsOpen} />;

  return (
    <div
      className="absolute top-4 flex items-center justify-center transition-all"
      style={{ right: metadataSidebarRight }}
    >
      <Tooltip asChild text="Show utility agencies summary">
        <Button
          alternate
          icon="help"
          size="icon"
          iconSize={18}
          aria-label="Show utility agencies summary"
          onClick={() => setIsOpen(true)}
          data-testid="agencies-summary-open-button"
        />
      </Tooltip>
    </div>
  );
}

function AgenciesSummary({ setIsOpen }: { setIsOpen: (open: boolean) => void }) {
  const map = useMap();
  const { setViewMode } = useViewMode();
  const [loadingState, setLoadingState] = useState<'reset' | 'loading' | 'loaded'>('reset');

  const { viewState } = useSnapshot(mapState);
  const metadataSidebarRight = useMetadataSidebarRightOffset();
  const containerDiv = useRef<HTMLDivElement>(null);

  const summary = useAsyncRetry(async () => {
    /**
    This short-circuit helps with debugging without making a call 
    to the backend every time.
     
    setLoadingState('loaded');
    return {
      totalAgencies: 10,
      assetTypes: 10,
      availableDigitally: 20,
      availableForRequest: 20,
    };
    */
    if (viewState.zoom > MIN_ZOOM) {
      const bounds = map.current!.getBounds();

      const multiPolygon: MultiPolygon = {
        type: 'MultiPolygon',
        coordinates: [
          [
            [
              bounds!.getNorthWest().toArray(),
              bounds!.getNorthEast().toArray(),
              bounds!.getSouthEast().toArray(),
              bounds!.getSouthWest().toArray(),
              bounds!.getNorthWest().toArray(),
            ],
          ],
        ],
      };
      try {
        const summary = await getAgenciesSummary(multiPolygon);
        setLoadingState('loaded');
        return summary;
      } catch (e) {
        console.error(e, 'getAgenciesSummary failed');
        setLoadingState('reset');
        return null;
      }
    } else {
      return null;
    }
  });

  const [, cancel, reset] = useTimeoutFn(summary.retry, 900);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (viewState.zoom > MIN_ZOOM) {
      setLoadingState('loading');
      reset();
    } else {
      setLoadingState('reset');
      cancel();
    }
  }, [viewState.longitude]);

  let content = null;
  let width = '0rem';

  if (loadingState === 'reset') {
    content = null;
  }

  if (loadingState === 'loading') {
    content = (
      <Tooltip
        delayDuration={0}
        text="Loading agency information in current map area..."
        aria-label="Loading agencies"
        data-testid="agencies-summary-loading"
        triggerClassName="cursor-help p-3"
      >
        <CircularProgress indeterminate />
      </Tooltip>
    );
    width = '4rem';
  }

  if (loadingState === 'loaded' && summary.value) {
    width = '24rem';

    if (summary.value.totalAgencies === 0) {
      content = (
        <div className="p-3 transition-all">
          CivilGrid does not operate in this location. Please contact support if you have any questions.
        </div>
      );
    } else {
      content = (
        <div className="group h-[175px] overflow-hidden p-3 transition-all">
          <div className="mb-2 text-center">Utility Agencies Summary</div>

          <div className="absolute right-1 top-1">
            <Tooltip asChild text="Close utility agencies summary">
              <CloseIconButton
                onClick={() => setIsOpen(false)}
                aria-label="Close utility agencies summary"
                data-testid="agencies-summary-close-button"
              />
            </Tooltip>
          </div>

          <div className="grid-row-1 grid grid-cols-4">
            <SummaryPill
              side="left"
              label="Total Agencies"
              value={summary.value.totalAgencies}
              tooltip="List of unique utility agencies within the current map area. Note that some entities may appear multiple times in the list due to differences in legal or operating names, or slight differences in how they are reported in various databases. Create a project to view detailed utility information."
            />
            <SummaryPill
              side="left"
              label="Asset Types"
              value={summary.value.assetTypes}
              tooltip="Number of unique types of utility assets."
            />
            <SummaryPill
              side="right"
              label="Available Digitally"
              value={summary.value.availableDigitally}
              tooltip="Number of agencies with digital data on CivilGrid, meaning you can view it in the live map or download to CAD/GIS."
            />
            <SummaryPill
              side="right"
              label="Available for Request"
              value={summary.value.availableForRequest}
              last={true}
              tooltip="Number of agencies where you can request Maps and As-builts through our premium CivilSearch service. CivilGrid will handle utility requests on your behalf, managing the entire process from start to finish."
            />
          </div>
          <div className="mt-4 text-xs leading-tight">
            This is a summary of agencies that operate in the current map area. To view detailed agency information,{' '}
            <span
              className="cursor-pointer text-blue-50 underline hover:text-blue-60"
              onClick={() => {
                setViewMode('map');
                homeMapStore.setNewProjectMode(true);
              }}
            >
              create a project
            </span>{' '}
            here.
          </div>
        </div>
      );
    }
  }
  if (content) {
    return (
      <div ref={containerDiv} className="absolute top-4" style={{ right: metadataSidebarRight }}>
        <div
          className="flex items-center justify-center rounded-sm border border-grey-30 bg-white shadow transition-all"
          style={{ width }}
        >
          {content}
        </div>
      </div>
    );
  } else {
    return null;
  }
}
